import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";
import { terminate, saveNodeType, isHoverForInteractiveType } from "./utils";
import EachAndrewsPitchfork from "./wrapper/EachAndrewsPitchfork";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";

class AndrewsPitchfork extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawPitchfork = this.handleDrawPitchfork.bind(this);

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.handlePoint0Drag = this.handlePoint0Drag.bind(this);
    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);

    this.terminate = terminate.bind(this);
    this.getSelectionState = isHoverForInteractiveType("pitchforks").bind(this);

    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = [];

    this.state = {
      current: null,
      override: null,
    };
  }

  // Drawing lifecycle - Drag for first 2 points, click for 3rd
  handleDrawPitchfork(xyValue) {
    const { current } = this.state;

    if (isDefined(current) && isDefined(current.x0)) {
      if (isNotDefined(current.x1) || !current.waitingForThirdPoint) {
        // Dragging to second point (before release)
        this.mouseMoved = true;
        this.setState({
          current: {
            ...current,
            x1: xyValue[0],
            y1: xyValue[1],
          },
        });
      } else if (current.waitingForThirdPoint) {
        // Preview third point after drag is complete
        this.setState({
          current: {
            ...current,
            x2: xyValue[0],
            y2: xyValue[1],
          },
        });
      }
    }
  }

  handleStart(xyValue, moreProps) {
    const { current } = this.state;

    // Only start new drawing if not already drawing
    if (isNotDefined(current) || isNotDefined(current.x0)) {
      this.mouseMoved = false;
      this.setState(
        {
          current: {
            x0: xyValue[0],
            y0: xyValue[1],
            x1: null,
            y1: null,
            x2: null,
            y2: null,
            waitingForThirdPoint: false,
          },
        },
        () => {
          this.props.onStart(moreProps);
        },
      );
    }
  }

  handleEnd(xyValue, moreProps, e) {
    const { pitchforks, appearance } = this.props;
    const { current } = this.state;

    if (isNotDefined(current) || isNotDefined(current.x0)) return;

    // If we're still dragging the first line (haven't released yet)
    if (isNotDefined(current.x1) || !this.mouseMoved) {
      // Accidental click without drag - cancel
      this.setState({ current: null });
      return;
    }

    // If we just finished dragging to second point (p1)
    if (!current.waitingForThirdPoint) {
      // Finalize second point and wait for third point click
      this.setState({
        current: {
          ...current,
          x1: xyValue[0],
          y1: xyValue[1],
          x2: xyValue[0], // Initialize for preview
          y2: xyValue[1],
          waitingForThirdPoint: true,
        },
      });
      return;
    }

    // Final click - complete the pitchfork
    const newPitchfork = {
      x0: current.x0,
      y0: current.y0,
      x1: current.x1,
      y1: current.y1,
      x2: xyValue[0],
      y2: xyValue[1],
      selected: true,
      appearance: appearance,
    };

    const newPitchforks = pitchforks.concat(newPitchfork);

    this.setState(
      {
        current: null,
      },
      () => {
        this.props.onComplete(newPitchforks, moreProps, e);
      },
    );
  }

  // Drag handlers for existing pitchforks
  handleDrag(index, xy) {
    this.setState({
      override: {
        index,
        ...xy,
      },
    });
  }

  handlePoint0Drag(index, newXY) {
    const { pitchforks } = this.props;
    const item = pitchforks[index];

    this.setState({
      override: {
        index,
        x0: newXY[0],
        y0: newXY[1],
        x1: item.x1,
        y1: item.y1,
        x2: item.x2,
        y2: item.y2,
      },
    });
  }

  handlePoint1Drag(index, newXY) {
    const { pitchforks } = this.props;
    const item = pitchforks[index];

    this.setState({
      override: {
        index,
        x0: item.x0,
        y0: item.y0,
        x1: newXY[0],
        y1: newXY[1],
        x2: item.x2,
        y2: item.y2,
      },
    });
  }

  handlePoint2Drag(index, newXY) {
    const { pitchforks } = this.props;
    const item = pitchforks[index];

    this.setState({
      override: {
        index,
        x0: item.x0,
        y0: item.y0,
        x1: item.x1,
        y1: item.y1,
        x2: newXY[0],
        y2: newXY[1],
      },
    });
  }

  handleDragComplete(moreProps) {
    const { pitchforks } = this.props;
    const { override } = this.state;

    if (isDefined(override)) {
      const { index, ...rest } = override;

      const newPitchforks = pitchforks.map((each, idx) =>
        idx === index ? { ...each, ...rest, selected: true } : each,
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newPitchforks, moreProps);
        },
      );
    }
  }

  render() {
    const { enabled, pitchforks, appearance, hoverText } = this.props;
    const { current, override } = this.state;

    const hoverTextWithDefaults = {
      ...AndrewsPitchfork.defaultProps.hoverText,
      ...hoverText,
    };

    const tempPitchfork =
      isDefined(current) && isDefined(current.x0) ? (
        <EachAndrewsPitchfork
          interactive={false}
          {...current}
          appearance={appearance}
          hoverText={hoverTextWithDefaults}
        />
      ) : null;

    return (
      <g>
        {pitchforks.map((each, idx) => {
          // Deep merge appearance - instance colors should override base appearance
          const eachAppearance = isDefined(each.appearance)
            ? {
                ...appearance,
                ...each.appearance,
                // Handle stroke: can be string '#color' or object {color, width, dash, opacity}
                stroke:
                  typeof each.appearance.stroke === "string"
                    ? { ...appearance.stroke, color: each.appearance.stroke }
                    : typeof each.appearance.stroke === "object"
                      ? { ...appearance.stroke, ...each.appearance.stroke }
                      : appearance.stroke,
              }
            : appearance;

          const props = {
            key: idx,
            ref: this.saveNodeType(idx),
            index: idx,
            ...each,
            appearance: eachAppearance,
            hoverText: hoverTextWithDefaults,

            selected: each.selected,
            hovering: each.hovering,

            onDrag: this.handleDrag,
            onDragComplete: this.handleDragComplete,

            onPoint0Drag: this.handlePoint0Drag.bind(this, idx),
            onPoint1Drag: this.handlePoint1Drag.bind(this, idx),
            onPoint2Drag: this.handlePoint2Drag.bind(this, idx),

            ...(isDefined(override) && override.index === idx ? override : {}),
          };

          return <EachAndrewsPitchfork {...props} />;
        })}
        {tempPitchfork}
        <MouseLocationIndicator
          enabled={enabled}
          snap={false}
          shouldDisableSnap={() => true}
          onMouseDown={this.handleStart}
          onMouseMove={this.handleDrawPitchfork}
          onClick={this.handleEnd}
          r={4}
          stroke={appearance.stroke?.color || appearance.stroke || "#FF6B6B"}
          strokeWidth={2}
          opacity={1}
          data-evo-id="interactive-andrews-pitchfork"
        />
      </g>
    );
  }
}

AndrewsPitchfork.propTypes = {
  enabled: PropTypes.bool.isRequired,
  pitchforks: PropTypes.array.isRequired,
  appearance: PropTypes.object.isRequired,

  onStart: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSelect: PropTypes.func,

  hoverText: PropTypes.object.isRequired,
};

AndrewsPitchfork.defaultProps = {
  pitchforks: [],
  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
};

export default AndrewsPitchfork;
